import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { getStripe } from '../providers/getStripe';
import { useAuth } from '../providers/AuthProvider';
import apiService from '../providers/ApiService';
import { CircularProgress } from '@mui/material';

const tiers = [
  {
    title: 'Tier 1',
    price: '6.99',
    description: ['50 cases included'],
    buttonText: 'Buy now',
    buttonVariant: 'contained',
    priceId: 'price_1S4oHlCt8hj6uNRH91lTYZw3'
  },
  {
    title: 'Tier 2',
    subheader: 'Recommended',
    price: '10.99',
    description: ['100 cases included'],
    buttonText: 'Buy now',
    buttonVariant: 'contained',
    priceId: 'price_1RpCJiCt8hj6uNRHJwS2ByPz'
  },
  {
    title: 'Tier 3',
    price: '15',
    description: ['150 cases included'],
    buttonText: 'Coming soon',
    buttonVariant: 'outlined', // Changed from 'disabled' to a valid MUI variant
    priceId: 'price_1RpCJxCt8hj6uNRHaHTCJPK4'
  }
];

async function handleCheckout(email, priceId) {
  const stripe = await getStripe();
  if (stripe) {
    const { error } = await stripe.redirectToCheckout({
      lineItems: [
        {
          price: priceId,
          quantity: 1
        }
      ],
      mode: 'subscription', // here you can specify the mode as either `payment` or `subscription` for one time payments or recurring payments respectively
      successUrl: `http://www.sca-cases.co.uk/success`,
      cancelUrl: `http://www.sca-cases.co.uk/tiers`,
      customerEmail: email
    });
    console.warn(error.message);
  }
}

// Helper function to extract the tier number from a tier string
const getTierNumber = (tierString) => {
  if (!tierString) return 0;
  // Handle different potential formats of tier string
  const match = tierString.toString().match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

export default function Pricing() {
  const { email } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  console.log(email);

  // Fetch user data function that can be called after saving
  const fetchUserProfile = async () => {
    if (!email) {
      // If no email is available, the user is not logged in
      setLoading(false);
      return;
    }

    try {
      const data = await apiService.get(`/user/${email}`);

      if (data) {
        console.log('User data fetched successfully:', data);
        setUserData(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [email]);

  const isLoggedIn = localStorage.getItem('accessToken');

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5, pt: { xs: 12, sm: 12 } }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading tiers...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      id="pricing"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 }
      }}>
      <Box
        sx={{
          width: { sm: '100%', md: '60%' },
          textAlign: { sm: 'left', md: 'center' }
        }}>
        <Typography component="h2" variant="h4" color="text.primary">
          Pricing
        </Typography>
        <br></br>
        {isLoggedIn ? (
          <Typography component="h3" variant="h8" color="text.primary">
            Please cancel your current subscription via the Profile Page before changing tier to
            prevent being charged twice.
          </Typography>
        ) : null}

        {/* <Typography variant="body1" color="text.secondary">
          Please create an account and sign in to continue
        </Typography> */}
      </Box>
      <Grid container spacing={3} alignItems="center" justifyContent="center">
        {tiers.map((tier) => (
          <Grid item key={tier.title} xs={12} sm={tier.title === 'Enterprise' ? 12 : 6} md={4}>
            <Card
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                border: tier.title === 'Tier 2' ? '1px solid' : undefined,
                borderColor: tier.title === 'Tier 2' ? 'primary.main' : undefined,
                background:
                  tier.title === 'Tier 2' ? 'linear-gradient(#033363, #021F3B)' : undefined
              }}>
              <CardContent>
                <Box
                  sx={{
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: tier.title === 'Tier 2' ? 'grey.100' : ''
                  }}>
                  <Typography component="h3" variant="h6">
                    {tier.title}
                  </Typography>
                  {tier.title === 'Tier 2' && (
                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label={tier.subheader}
                      size="small"
                      sx={{
                        background: (theme) => (theme.palette.mode === 'light' ? '' : 'none'),
                        backgroundColor: 'primary.contrastText',
                        '& .MuiChip-label': {
                          color: 'primary.dark'
                        },
                        '& .MuiChip-icon': {
                          color: 'primary.dark'
                        }
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    color: tier.title === 'Tier 2' ? 'grey.50' : undefined
                  }}>
                  <Typography component="h3" variant="h2">
                    £{tier.price}
                  </Typography>
                  <Typography component="h3" variant="h6">
                    &nbsp; per month
                  </Typography>
                </Box>
                <Divider
                  sx={{
                    my: 2,
                    opacity: 0.2,
                    borderColor: 'grey.500'
                  }}
                />
                {tier.description.map((line) => (
                  <Box
                    key={line}
                    sx={{
                      py: 1,
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center'
                    }}>
                    <CheckCircleRoundedIcon
                      sx={{
                        width: 20,
                        color: tier.title === 'Tier 2' ? 'primary.light' : 'primary.main'
                      }}
                    />
                    <Typography
                      component="text"
                      variant="subtitle2"
                      sx={{
                        color: tier.title === 'Tier 2' ? 'grey.200' : undefined
                      }}>
                      {line}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  variant={tier.buttonVariant}
                  disabled={
                    !isLoggedIn ||
                    tier.title === 'Tier 3' ||
                    (userData?.status === 'active' &&
                      userData?.tier === getTierNumber(tier.title)) ||
                    (userData?.status === 'active' && userData?.tier > getTierNumber(tier.title))
                  }
                  onClick={async () => await handleCheckout(email, tier.priceId)}>
                  {!isLoggedIn
                    ? 'Log in to Purchase'
                    : tier.title === 'Tier 3'
                      ? 'Coming soon'
                      : userData?.status === 'active'
                        ? userData?.tier === getTierNumber(tier.title)
                          ? 'Current Plan'
                          : userData?.tier && tier.title
                            ? getTierNumber(tier.title) > userData?.tier
                              ? 'Upgrade'
                              : tier.buttonText
                            : tier.buttonText
                        : tier.buttonText}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
